import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Card,
    ListGroup,
    Badge,
    Button,
    Modal,
    Form,
    Spinner,
    Alert,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import { useUserApi } from '@/shared/services/userApi';
import { useOrganizationApi } from '@/shared/services/organizationApi';

const AFFILIATION_TYPES = [
    {
        value: 'professional',
        label: 'Professional',
        description: 'Current work affiliation',
    },
    {
        value: 'alumni',
        label: 'Alumni',
        description: 'Former affiliation',
    },
    {
        value: 'volunteer',
        label: 'Volunteer',
        description: 'Volunteer relationship',
    },
    {
        value: 'collaborator',
        label: 'Collaborator',
        description: 'Project-based collaboration',
    },
    {
        value: 'interested',
        label: 'Interested',
        description: "Following this org's work",
    },
];

const TYPE_BADGE_COLORS = {
    professional: 'primary',
    alumni: 'secondary',
    volunteer: 'success',
    collaborator: 'info',
    interested: 'warning',
};

export default function AffiliationManager({ onUpdate, ecosystem }) {
    const [affiliations, setAffiliations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAffiliation, setSelectedAffiliation] = useState(null);
    const [imageErrors, setImageErrors] = useState({});

    // Add modal state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [affiliationType, setAffiliationType] = useState('professional');
    const [affiliationNote, setAffiliationNote] = useState('');
    const [isPublic, setIsPublic] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const userApi = useUserApi();
    const orgApi = useOrganizationApi();

    const fetchAffiliations = useCallback(async () => {
        try {
            setLoading(true);
            const data = await userApi.fetchMyAffiliations();
            setAffiliations(data.affiliations || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching affiliations:', err);
            setError('Failed to load affiliations');
        } finally {
            setLoading(false);
        }
    }, [userApi]);

    useEffect(() => {
        fetchAffiliations();
    }, [fetchAffiliations]);

    // Search organizations with debounce
    useEffect(() => {
        if (!searchQuery || searchQuery.length < 2) {
            setSearchResults([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setSearching(true);
                const data = await orgApi.searchOrganizations(searchQuery);
                // Filter out orgs user is already affiliated with
                const affiliatedOrgIds = affiliations.map((a) =>
                    a.organization._id.toString()
                );
                const filtered = (data.organizations || []).filter(
                    (org) => !affiliatedOrgIds.includes(org._id)
                );
                setSearchResults(filtered.slice(0, 10));
            } catch (err) {
                console.error('Error searching organizations:', err);
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery, orgApi, affiliations]);

    const handleAddAffiliation = async () => {
        if (!selectedOrg) return;

        try {
            setSubmitting(true);
            setSubmitError(null);

            await userApi.createAffiliation({
                organizationId: selectedOrg._id,
                type: affiliationType,
                note: affiliationNote,
                isPublic,
            });

            await fetchAffiliations();
            setShowAddModal(false);
            resetAddForm();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Error creating affiliation:', err);
            setSubmitError(
                err.response?.data?.message || 'Failed to create affiliation'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateAffiliation = async () => {
        if (!selectedAffiliation) return;

        try {
            setSubmitting(true);
            setSubmitError(null);

            await userApi.updateAffiliation(
                selectedAffiliation.organization._id,
                {
                    type: affiliationType,
                    note: affiliationNote,
                    isPublic,
                }
            );

            await fetchAffiliations();
            setShowEditModal(false);
            setSelectedAffiliation(null);
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Error updating affiliation:', err);
            setSubmitError(
                err.response?.data?.message || 'Failed to update affiliation'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAffiliation = async (orgId) => {
        if (
            !window.confirm('Are you sure you want to remove this affiliation?')
        ) {
            return;
        }

        try {
            await userApi.deleteAffiliation(orgId);
            await fetchAffiliations();
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Error deleting affiliation:', err);
            setError('Failed to delete affiliation');
        }
    };

    const resetAddForm = () => {
        setSearchQuery('');
        setSearchResults([]);
        setSelectedOrg(null);
        setAffiliationType('professional');
        setAffiliationNote('');
        setIsPublic(true);
        setSubmitError(null);
    };

    const openEditModal = (affiliation) => {
        setSelectedAffiliation(affiliation);
        setAffiliationType(affiliation.type);
        setAffiliationNote(affiliation.note || '');
        setIsPublic(affiliation.isPublic);
        setSubmitError(null);
        setShowEditModal(true);
    };

    if (loading) {
        return (
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                    <strong>My Network</strong>
                </Card.Header>
                <Card.Body className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                </Card.Body>
            </Card>
        );
    }

    return (
        <>
            <Card className="shadow-sm mt-4">
                <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
                    <div>
                        <strong>My Network</strong>
                        {ecosystem?.name && (
                            <div className="text-muted small">
                                Affiliations in {ecosystem.name}
                            </div>
                        )}
                    </div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        className="rounded-pill"
                        onClick={() => {
                            resetAddForm();
                            setShowAddModal(true);
                        }}
                    >
                        + Add Affiliation
                    </Button>
                </Card.Header>
                <Card.Body className="p-0">
                    {error && (
                        <Alert variant="danger" className="m-3">
                            {error}
                        </Alert>
                    )}

                    {affiliations.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            <p className="mb-2">
                                No affiliations yet. Connect with organizations
                                in {ecosystem?.name || 'this ecosystem'}.
                            </p>
                            <Button
                                variant="primary"
                                size="sm"
                                className="rounded-pill"
                                onClick={() => {
                                    resetAddForm();
                                    setShowAddModal(true);
                                }}
                            >
                                Add Your First Affiliation
                            </Button>
                        </div>
                    ) : (
                        <ListGroup variant="flush">
                            {affiliations.map((aff) => (
                                <ListGroup.Item
                                    key={aff.organization._id}
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <Link
                                        to={`/organizations/${aff.organization._id}`}
                                        className="text-decoration-none"
                                    >
                                        <div className="d-flex align-items-center gap-3">
                                            {aff.organization.imageUrl &&
                                            !imageErrors[
                                                aff.organization._id
                                            ] ? (
                                                <img
                                                    src={
                                                        aff.organization
                                                            .imageUrl
                                                    }
                                                    alt={aff.organization.name}
                                                    className="rounded org-thumbnail-sm"
                                                    onError={() =>
                                                        setImageErrors(
                                                            (prev) => ({
                                                                ...prev,
                                                                [aff
                                                                    .organization
                                                                    ._id]: true,
                                                            })
                                                        )
                                                    }
                                                />
                                            ) : (
                                                <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-sm">
                                                    <span className="text-muted">
                                                        {aff.organization.name.charAt(
                                                            0
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                            <div>
                                                <strong>
                                                    {aff.organization.name}
                                                </strong>
                                                <br />
                                                <small className="text-muted">
                                                    {
                                                        aff.organization
                                                            .primarySector
                                                    }
                                                </small>
                                            </div>
                                        </div>
                                    </Link>
                                    <div className="d-flex align-items-center gap-2">
                                        <Badge
                                            bg={
                                                TYPE_BADGE_COLORS[aff.type] ||
                                                'secondary'
                                            }
                                        >
                                            {aff.type}
                                        </Badge>
                                        {!aff.isPublic && (
                                            <Badge bg="dark" title="Private">
                                                Private
                                            </Badge>
                                        )}
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-muted p-0"
                                            onClick={() => openEditModal(aff)}
                                            title="Edit"
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="text-danger p-0"
                                            onClick={() =>
                                                handleDeleteAffiliation(
                                                    aff.organization._id
                                                )
                                            }
                                            title="Remove"
                                        >
                                            Remove
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Card.Body>
            </Card>

            {/* Add Affiliation Modal */}
            <Modal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Add Affiliation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submitError && (
                        <Alert variant="danger">{submitError}</Alert>
                    )}

                    {!selectedOrg ? (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>Search Organizations</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Type to search..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                />
                            </Form.Group>

                            {searching && (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            )}

                            {searchResults.length > 0 && (
                                <ListGroup>
                                    {searchResults.map((org) => (
                                        <ListGroup.Item
                                            key={org._id}
                                            action
                                            onClick={() => setSelectedOrg(org)}
                                            className="d-flex align-items-center gap-2"
                                        >
                                            <div className="rounded bg-light d-flex align-items-center justify-content-center org-placeholder-xs">
                                                <span className="text-muted small">
                                                    {org.name.charAt(0)}
                                                </span>
                                            </div>
                                            <div>
                                                <strong>{org.name}</strong>
                                                <br />
                                                <small className="text-muted">
                                                    {org.primarySector}
                                                </small>
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            )}

                            {searchQuery.length >= 2 &&
                                !searching &&
                                searchResults.length === 0 && (
                                    <p className="text-muted text-center">
                                        No organizations found
                                    </p>
                                )}
                        </>
                    ) : (
                        <>
                            <div className="mb-3 p-3 bg-light rounded">
                                <strong>{selectedOrg.name}</strong>
                                <br />
                                <small className="text-muted">
                                    {selectedOrg.primarySector}
                                </small>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ms-2"
                                    onClick={() => setSelectedOrg(null)}
                                >
                                    Change
                                </Button>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Affiliation Type</Form.Label>
                                <Form.Select
                                    value={affiliationType}
                                    onChange={(e) =>
                                        setAffiliationType(e.target.value)
                                    }
                                >
                                    {AFFILIATION_TYPES.map((type) => (
                                        <option
                                            key={type.value}
                                            value={type.value}
                                        >
                                            {type.label} - {type.description}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Note (optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="e.g., Teaching STEM curriculum"
                                    value={affiliationNote}
                                    onChange={(e) =>
                                        setAffiliationNote(e.target.value)
                                    }
                                    maxLength={200}
                                />
                                <Form.Text className="text-muted">
                                    {affiliationNote.length}/200 characters
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="public-switch"
                                    label="Show publicly on my profile"
                                    checked={isPublic}
                                    onChange={(e) =>
                                        setIsPublic(e.target.checked)
                                    }
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowAddModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddAffiliation}
                        disabled={!selectedOrg || submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Adding...
                            </>
                        ) : (
                            'Add Affiliation'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Affiliation Modal */}
            <Modal
                show={showEditModal}
                onHide={() => setShowEditModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Edit Affiliation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {submitError && (
                        <Alert variant="danger">{submitError}</Alert>
                    )}

                    {selectedAffiliation && (
                        <>
                            <div className="mb-3 p-3 bg-light rounded">
                                <strong>
                                    {selectedAffiliation.organization.name}
                                </strong>
                                <br />
                                <small className="text-muted">
                                    {
                                        selectedAffiliation.organization
                                            .primarySector
                                    }
                                </small>
                            </div>

                            <Form.Group className="mb-3">
                                <Form.Label>Affiliation Type</Form.Label>
                                <Form.Select
                                    value={affiliationType}
                                    onChange={(e) =>
                                        setAffiliationType(e.target.value)
                                    }
                                >
                                    {AFFILIATION_TYPES.map((type) => (
                                        <option
                                            key={type.value}
                                            value={type.value}
                                        >
                                            {type.label} - {type.description}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Note (optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="e.g., Teaching STEM curriculum"
                                    value={affiliationNote}
                                    onChange={(e) =>
                                        setAffiliationNote(e.target.value)
                                    }
                                    maxLength={200}
                                />
                                <Form.Text className="text-muted">
                                    {affiliationNote.length}/200 characters
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="edit-public-switch"
                                    label="Show publicly on my profile"
                                    checked={isPublic}
                                    onChange={(e) =>
                                        setIsPublic(e.target.checked)
                                    }
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowEditModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUpdateAffiliation}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

AffiliationManager.propTypes = {
    onUpdate: PropTypes.func,
    // Optional explicit ecosystem context, used by the per-membership
    // accordion to scope the header label ("Affiliations in {ecosystem.name}")
    // and the empty-state copy. The underlying API calls always run under the
    // page's active ecosystem (via the axios singleton's basePath), so this
    // is only passed for the ACTIVE membership.
    ecosystem: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
    }),
};

AffiliationManager.defaultProps = {
    onUpdate: null,
    ecosystem: null,
};
